import React from "react";
import "../common/NoticesAndLitigationDetail.css";
import { useState, useEffect, useRef } from "react";
import downloadButton from "./../../../../assets/img/icon/downloadButton.svg";
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import EditDetails from "./LitigationsEdit";
import { useUpdateLitigation, useChangeLitigationStatus } from "../../../../backend/litigation";
import { hasUserAccess, getUserDetails } from "../../../../backend/auth";
import { toast } from "../../../../utils/toast";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";
import PageLoader from "../../../shared/PageLoader";
import {
  useGetLitigationHistory,
  useGetLitigation,
} from "../../../../backend/litigation";
import { useQueryClient } from "@tanstack/react-query";
import { downloadS3FileSecurely, formatDateDisplay } from "../../../../utils/common";
import {
  getAllowedStatusTransitions,
  getStatusLabel,
  getStatusColorClass,
  formatDateTime
} from "../../../../backend/common/noticeManagementUtils";

interface LicenseDetailProps {
  isOpen: boolean;
  closePanel: () => void;
  license?: any;
  refetch?: () => void;
}

interface HistoryChange {
  from: any;
  to: any;
  reason?: string;
}

interface HistoryItem {
  id: number | string;
  action: string;
  entry_type?: string;
  changed_by: string;
  changed_at: string;
  changes?: Record<string, HistoryChange>;
  document_details?: {
    filename: string;
    file_size: string;
    content_type: string;
  };
}

interface DisplayHistoryItem {
  id: number | string;
  title: string;
  date: string;
  rawDate: string;
  description: string;
  name: string;
}

const LitigationDetail: React.FC<LicenseDetailProps> = ({
  isOpen,
  closePanel,
  license,
  refetch,
}) => {
  const [activeTab, setActiveTab] = useState<
    "details" | "history" | "documents"
  >("details");
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>("open");
  const [statusNote, setStatusNote] = useState<string>("");
  const [showStatusPopup, setShowStatusPopup] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState<string>("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const statusNoteRef = useRef<HTMLTextAreaElement>(null);

  const user = getUserDetails();
  const queryClient = useQueryClient();

  const { litigation: fetchedLitigation, isFetching: isFetchingLitigation, refetch: refetchLitigation } = useGetLitigation(
    license && license.id ? license.id : "",
    !!(license && license.id && isOpen)
  );

  // Use fetched data when available, fallback to prop
  const displayLitigation = fetchedLitigation && Object.keys(fetchedLitigation).length > 0
    ? fetchedLitigation
    : license;


  const { updateLitigation, updating } = useUpdateLitigation(
    (response: any) => {
      toast.success("Litigation updated successfully");
      closeEditPopup();
      closePanel();
      queryClient.invalidateQueries(['litigations']);
      queryClient.invalidateQueries(['litigation', license && license.id]);
      if (refetch) refetch();
      if (refetchLitigation) refetchLitigation();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && (errorData.error_messages || errorData.errors || errorData.details);

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg);
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update litigation");
      }
    }
  );

  // Status change mutation hook
  const { changeStatus, updating: updatingStatus } = useChangeLitigationStatus(
    () => {
      toast.success("Litigation status updated successfully");
      queryClient.invalidateQueries(['litigations']);
      queryClient.invalidateQueries(['litigation', license && license.id]);
      if (refetch) refetch();
      if (refetchLitigation) refetchLitigation();
      setShowStatusPopup(false);
      setStatusNote("");
      setPendingStatusChange("");
      closePanel();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && (errorData.error_messages || errorData.errors || errorData.details);

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg);
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update litigation status");
      }
    }
  );

  useEffect(() => {
    if (displayLitigation && displayLitigation.status) {
      setSelectedStatus(displayLitigation.status);
    }
  }, [displayLitigation]);

  useEffect(() => {
    if (displayLitigation && selectedStatus === displayLitigation.status) {
      setStatusNote("");
    }
  }, [selectedStatus, displayLitigation]);

  // Reset to details tab when panel opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab("details");
    }
  }, [isOpen]);

  // Scroll to top when tab changes
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  // Scroll to status note textarea when it appears
  useEffect(() => {
    if (showStatusPopup && statusNoteRef.current) {
      setTimeout(() => {
        if (statusNoteRef.current) {
          statusNoteRef.current.focus();
          statusNoteRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 150);
    }
  }, [showStatusPopup]);

  const litigationDocuments = displayLitigation && displayLitigation.documents
    ? displayLitigation.documents
    : [];

  const isLoading = updating || updatingStatus || isFetchingLitigation;

  const { historyItems } = useGetLitigationHistory(
    displayLitigation && displayLitigation.id,
    undefined,
    !!(displayLitigation && displayLitigation.id)
  );


  const handleStatusChange = (newStatus: string) => {
    if (newStatus === selectedStatus) return;
    setPendingStatusChange(newStatus);
    setStatusNote("");
    setShowStatusPopup(true);
  };

  const handleSaveStatusChange = async () => {
    if (!statusNote.trim()) {
      toast.error("Please add a note for the status change");
      return;
    }

    if (!displayLitigation || !displayLitigation.id) return;

    const payload = {
      id: displayLitigation.id,
      status: pendingStatusChange === "resolved" ? "resolved" : pendingStatusChange,
      note: statusNote,
    };

    try {
      await changeStatus(payload as any);
    } catch (error) {
      console.error("Error updating status:", error);
    }
  };

  const handleCancelStatusChange = () => {
    setShowStatusPopup(false);
    setStatusNote("");
    setPendingStatusChange("");
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    if (!fileUrl) {
      toast.error("File URL not available");
      return;
    }
    try {
      await downloadS3FileSecurely(fileName, fileUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      toast.error("Failed to download file");
    }
  };

  const licenseDetails = displayLitigation ? [
    { label: "Litigation ID", value: displayLitigation.id || "-" },
    { label: "Litigation Name", value: displayLitigation.name },
    { label: "Description", value: displayLitigation.description || "-" },
    { label: "Issued Date", value: formatDateDisplay(displayLitigation.issued_date) },
    { label: "Resolution Date", value: formatDateDisplay(displayLitigation.resolution_date || displayLitigation.expiry_date) },
    {
      label: "Company",
      value: (displayLitigation.company && displayLitigation.company.name) || "-",
    },
    {
      label: "Associate Company",
      value:
        (displayLitigation.associate_company && displayLitigation.associate_company.name) ||
        "-",
    },
    {
      label: "Location",
      value: displayLitigation.location  ? `${displayLitigation.location.name} - ${displayLitigation.location.code}` : "-",
    },
    {
      label: "Category",
      value: (displayLitigation.category && displayLitigation.category.name) || "-",
    },
    { label: "Issued By", value: displayLitigation.issued_by || displayLitigation.authority || "-" },
    {
      label: "Issued Under Act",
      value: displayLitigation.act  ? `${displayLitigation.act.establishment_type} - ${displayLitigation.act.name}` : "-",
    },
    { label: "Court Name", value: displayLitigation.court_name || "-" },
    { label: "Case Number", value: displayLitigation.case_number || "-" },
    { label: "Next Hearing Date", value: formatDateDisplay(displayLitigation.next_hearing_date) },
    { label: "Advocate Details", value: displayLitigation.advocate_details || "-" },
    { label: "Case Status", value: displayLitigation.case_status || "-" },
    { label: "Assigned To", value: (displayLitigation.owner && displayLitigation.owner.name) || "-" },
    {
      label: "Watchers",
      value:
        (displayLitigation.watchers && displayLitigation.watchers.map((watcher: any) => watcher.name).join(", ")) ||
        "-",
    },
  ] : [];

  const formatFieldName = (field: string): string => {
    // Remove _id suffix for cleaner field names
    const cleanField = field.replace(/_id$/, '');
    return cleanField
      .replace(/_/g, ' ')
      .split(' ')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatFieldValue = (value: any, field?: string): string => {
    if (value === null || value === undefined) return "(empty)";
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'string') {
      if (value.trim() === '') return "(empty)";

      if (field === 'status') {
        // Format status values: remove underscores and capitalize
        return value === 'in_progress' ? 'In Progress' :
               value === 'on_hold' ? 'On Hold' :
               value === 'resolved' ? 'Resolved' :
               value === 'open' ? 'Open' :
               value.replace(/_/g, ' ').split(' ').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
      }

      if (field === 'category_id' && displayLitigation && displayLitigation.category) {
        if (value === displayLitigation.category.id) {
          return displayLitigation.category.name;
        }
        return value;
      }

      if (field && (field.includes('_date') || field === 'date') && !isNaN(Date.parse(value))) {
        return formatDateDisplay(value);
      }
      return value;
    }

    if (typeof value === 'object' && value.id && value.name) {
      return value.name;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return "None";
      return value.map(item => {
        if (typeof item === 'object' && item.name) {
          return item.name;
        }
        return String(item);
      }).join(', ');
    }

    return String(value);
  };

  const displayHistoryItems: DisplayHistoryItem[] = [];

  if (displayLitigation) {
    // 2. Litigation Edited - Create ONE entry per change object with all fields
    if (historyItems && historyItems.length > 0) {
      historyItems.forEach((historyItem: HistoryItem, historyIndex: number) => {
        if (historyItem.changes && Object.keys(historyItem.changes).length > 0) {
          const relevantFields = Object.keys(historyItem.changes).filter((field: string) => field !== 'metadata');

          if (relevantFields.length === 0) {
            return;
          }

          const isStatusChange = relevantFields.length === 1 && relevantFields[0] === 'status';

          if (isStatusChange) {
            const statusChange = historyItem.changes.status;
            const fromStatus = statusChange.from === 'in_progress' ? 'In Progress' :
                              statusChange.from === 'on_hold' ? 'On Hold' :
                              statusChange.from === 'resolved' ? 'Resolved' :
                              statusChange.from === 'open' ? 'Open' : statusChange.from;
            const toStatus = statusChange.to === 'in_progress' ? 'In Progress' :
                            statusChange.to === 'on_hold' ? 'On Hold' :
                            statusChange.to === 'resolved' ? 'Resolved' :
                            statusChange.to === 'open' ? 'Open' : statusChange.to;

            displayHistoryItems.push({
              id: 1000 + historyIndex,
              title: "Status changes",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `Status changed from <strong class="text-capitalize">${fromStatus}</strong> to <strong class="text-capitalize">${toStatus}</strong> by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
              description: `Reason: ${statusChange && statusChange.reason}`,
            });
          } else {
            // Build description with all field changes
            const changesList = relevantFields
              .map((field: string) => {
                const change = historyItem.changes![field];
                const fieldLabel = formatFieldName(field);
                const fromValue = formatFieldValue(change.from, field);
                const toValue = formatFieldValue(change.to, field);
                return `${fieldLabel} changed from <strong>${fromValue}</strong> to <strong>${toValue}</strong>`;
              })
              .join('<br/>');

            // Check if status is among the changed fields
            const hasStatusChange = relevantFields.includes('status');
            const statusReason = hasStatusChange && historyItem.changes!.status && historyItem.changes!.status.reason
              ? `Reason: ${historyItem.changes!.status.reason}`
              : "";

            displayHistoryItems.push({
              id: 1000 + historyIndex,
              title: "Litigation Edited",
              date: formatDateTime(historyItem.changed_at),
              rawDate: historyItem.changed_at,
              name: `${changesList} by <strong style="color:#2965AD" class="text-capitalize">${historyItem.changed_by || "-"}</strong>`,
              description: statusReason,
            });
          }
        }
      });
    }

    // 3. Document Events (Upload/Delete) - Grouped by timestamp and action
    if (historyItems && historyItems.length > 0) {
      const documentEvents = historyItems.filter((item: HistoryItem) =>
        item.entry_type === 'document_event' && item.document_details
      );

      // Group by timestamp and action
      const groupedEvents: Record<string, HistoryItem[]> = {};
      documentEvents.forEach((item: HistoryItem) => {
        const key = `${item.changed_at}_${item.action}`;
        if (!groupedEvents[key]) {
          groupedEvents[key] = [];
        }
        groupedEvents[key].push(item);
      });

      // Create history items for each group
      Object.keys(groupedEvents).forEach((key) => {
        const group = groupedEvents[key];
        if (group.length === 0) return;

        const firstItem = group[0];
        const action = firstItem.action;

        // Build list of files with appropriate prefix
        let displayName = '';
        if (action === 'uploaded') {
          const filesList = group
            .map((item: HistoryItem) => {
              const { filename, file_size } = item.document_details!;
              return `<strong style='word-break: break-all;'>${filename}</strong> (${file_size})`;
            })
            .join('<br/>');
          displayName = `New document(s) have been uploaded:<br/>${filesList} by <strong style="color:#2965AD" class="text-capitalize">${firstItem.changed_by || "-"}</strong>`;
        } else {
          // For deleted files
          const prefix = group.length === 1 ? 'A file has been deleted:' : 'Files have been deleted:';
          const filesList = group
            .map((item: HistoryItem) => {
              const { filename, file_size } = item.document_details!;
              return `<strong style='word-break: break-all;'>${filename}</strong> (${file_size})`;
            })
            .join('<br/>');
          displayName = `${prefix}<br/> ${filesList} by <strong style="color:#2965AD" class="text-capitalize">${firstItem.changed_by || "-"}</strong>`;
        }

        displayHistoryItems.push({
          id: key,
          title: action === 'uploaded' ? 'Document(s) Uploaded' : 'Document Deleted',
          date: formatDateTime(firstItem.changed_at),
          rawDate: firstItem.changed_at,
          name: displayName,
          description: '',
        });
      });
    }

    // 4. Litigation Created
    displayHistoryItems.push({
      id: 3000,
      title: "Litigation Created",
      date: formatDateTime(displayLitigation.created_at),
      rawDate: displayLitigation.created_at,
      name: `Created by <strong style="color:#2965AD" class="text-capitalize">${displayLitigation.creator && displayLitigation.creator.name || "-"}</strong>`,
      description: "",
    });
  }

  // Sort by date (newest first)
  displayHistoryItems.sort((a, b) => {
    const dateA = new Date(a.rawDate || 0).getTime();
    const dateB = new Date(b.rawDate || 0).getTime();
    return dateB - dateA;
  });


  const handleClosePanel = () => {
    setActiveTab("details");
    setShowStatusPopup(false);
    setStatusNote("");
    setPendingStatusChange("");
    closePanel();
  };

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const handleSave = (data: any, section?: string) => {
    if (!displayLitigation || !displayLitigation.id) return;

    const payload: any = {
      id: displayLitigation.id,
    };

    // Always include litigation object at top level
    payload.litigation = {
      ...(data.litigation || {}),
      updated_by_id: user.user_id,
    };

    // Include document_details if present
    if (data.document_details) {
      payload.document_details = data.document_details;
    }

    updateLitigation(payload as any);
  };

  return (
    <>
      {isOpen && (
        <div
          className="licensedetails-overlay"
          onClick={handleClosePanel}
        ></div>
      )}
      <div className="licensedetails-wrapper  notice-wrapper">
        <div className={`licensedetails ${isOpen ? "open" : ""}`} ref={scrollContainerRef}>
          <div className="licensedetails-header pb-0">
            <div className="d-flex justify-content-between align-items-start">
              <h5 className="mb-0 fw-semibold">{displayLitigation && displayLitigation.name}</h5>
              <div className="d-flex justify-content-between align-items-center gap-4">
                {(hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) || hasUserAccess(USER_PRIVILEGES.EDIT_LITIGATIONS)) && (displayLitigation && displayLitigation.status !== 'resolved') && (
                  <div
                    className="d-flex align-items-center gap-2 cursor-pointer"
                    onClick={(e) => {
                      if (displayLitigation && displayLitigation.status === 'resolved') {
                        e.preventDefault();
                        return;
                      }
                      openEditPopup();
                    }}
                  >
                    <img src={editIcon} alt="editIcon" />
                    <span className="edit_button">Edit</span>
                  </div>
                )}
                <button
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleClosePanel}
                ></button>
              </div>
            </div>

            <div>
              <ul className="progress-section p-0 m-0 mt-4 pl-2">
                <li
                  className={activeTab === "details" ? "active" : ""}
                  onClick={() => setActiveTab("details")}
                >
                  Details
                </li>
                {(litigationDocuments && litigationDocuments.length !== 0) && (
                  <li
                    className={activeTab === "documents" ? "active" : ""}
                    onClick={() => setActiveTab("documents")}
                  >
                    Documents
                </li>)}
                <li
                  className={activeTab === "history" ? "active" : ""}
                  onClick={() => setActiveTab("history")}
                >
                  History
                </li>
              </ul>
            </div>
          </div>            
          <div className="licensedetails-body" style={{height: "calc(100% + 260px)"}}>

            {/* License Details Tab */}
            {activeTab === "details" && (
              <>
                <ul className="licensedetails-text pb-3">
                {licenseDetails.map((item, index) => (
                  <li key={index}>
                    <div>{item.label}</div>
                      <div style={item.label === "Description" ? { whiteSpace: 'pre-wrap' } : undefined}>{item.value}</div>
                  </li>
                ))}
                <li>
                  <div>Litigation Status</div>
                  {(hasUserAccess(USER_PRIVILEGES.NOTICE_MANAGER) || hasUserAccess(USER_PRIVILEGES.NOTICE_OWNER) || hasUserAccess(USER_PRIVILEGES.EDIT_NOTICES)) ? (
                    <div className="w-100">
                      <select
                        className="licensedetails-select form-select text-capitalize"
                        value={showStatusPopup ? pendingStatusChange : selectedStatus}
                        onChange={(e) => handleStatusChange(e.target.value)}
                        style={{ width: "180px" }}
                        disabled={selectedStatus === "resolved"}
                      >
                        <option value={selectedStatus}>{getStatusLabel(selectedStatus)}</option>
                        {getAllowedStatusTransitions(selectedStatus).map((status) => (
                          <option key={status} value={status}>
                            {getStatusLabel(status)}
                          </option>
                        ))}
                      </select>
                      {selectedStatus === "resolved" && (
                        <div className="text-muted small mt-1">
                          Resolved litigations cannot be changed to any other status.
                        </div>
                      )}
                      {showStatusPopup && (
                        <div className="mt-3 w-100">
                          <label className="mb-2">
                            Reason for status change<span>*</span>
                          </label>
                          <textarea
                            ref={statusNoteRef}
                            className="form-control"
                            placeholder="Add reason for status change"
                            maxLength={1000}
                            rows={4}
                            style={{minHeight: "114px"}}
                            value={statusNote}
                            onChange={(e) => setStatusNote(e.target.value)}
                          ></textarea>
                          <small className="text-muted text-sm d-flex justify-content-end">
                            {statusNote.length}/1000 characters
                          </small>
                          <div className="d-flex gap-2 mt-2">
                            <button
                              type="button"
                              className="button-primary w-100"
                              onClick={handleSaveStatusChange}
                              disabled={isLoading || !statusNote.trim()}
                            >
                              {updatingStatus ? "Updating..." : "Confirm Status Update"}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <span className={`text-capitalize fw-bold color-class ${getStatusColorClass(selectedStatus)}`}>
                      {getStatusLabel(selectedStatus)}
                    </span>
                  )}
                </li>
                {(displayLitigation && displayLitigation.rejection_reason) && 
                  <li>
                    <div></div>
                    <div className="text-capitalize text-md">
                      <span className="d-block" style={{color: "#374151", fontWeight: "400"}}>Reason:</span><div className="fw-medium">{displayLitigation && displayLitigation.rejection_reason || "-"}</div></div>
                  </li>
                }
              </ul>
              </>
            )}

            {activeTab === "documents" && (
              <>
                <div className="document-container pt-4">
                  {litigationDocuments && litigationDocuments.length > 0 ? (
                    <div className="document-list">
                      <div className="fw-bold mb-3">Attached Documents</div>
                      {litigationDocuments.map((doc: any) => {

                        var fileName = doc.name || "No file";
                        var fileUrl = doc.file_path || "";

                        return (
                          <div key={doc.id} className="document-item mb-4">
                            <div className="d-flex justify-content-between align-items-center">
                              {/* <label className="form-label w-50 ellipse">
                                {documentName}
                              </label> */}
                              <div className="upload-successful position-relative w-100">
                              <div className="px-4 py-3 d-flex align-items-center justify-content-between">
                                <p className="text-sm fw-semibold m-0 img_name_truncate">
                                  {fileName}
                                </p>
                                <div
                                  style={{
                                    cursor: "pointer",
                                    height: "25px",
                                    width: "25px",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDownload(fileUrl, fileName);
                                  }}
                                >
                                  <img
                                    className="d-block"
                                    src={downloadButton}
                                    alt="downloadButton"
                                  />
                                </div>
                              </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted h-100 w-100 d-flex justify-content-center align-items-center">
                      No documents uploaded for this litigation.
                    </p>
                  )}
                </div>
              </>
            )}

            {/* History Tab */}
            {activeTab === "history" && (
              <>
                <div className="history-accordion">
                  {displayHistoryItems.map((item, index) => (
                    <div key={item.id} className="accordion-item mb-2">
                      <div className="accordion-timeline">
                        <div
                          className={`timeline-dot d-flex justify-content-center align-items-center ${
                            item ? "active" : ""
                          }`}
                        >
                          <div className="progress-checked"></div>{" "}
                        </div>
                        {index < displayHistoryItems.length - 1 && (
                          <div className="timeline-line">
                            <div
                              className={`dark-line ${
                                expandedId === item.id ? "expanded" : ""
                              }`}
                            ></div>
                          </div>
                        )}
                      </div>
                      <div className="accordion-content-wrapper">
                        <div
                          className="accordion-header"
                        >
                          <div className="accordion-header-left">
                            <h6
                              className="accordion-title mb-0"
                              dangerouslySetInnerHTML={{ __html: item.title }}
                            ></h6>
                            {item.name && <span className="accordion-name" dangerouslySetInnerHTML={{ __html: item.name }}></span>}
                            {item.description && (
                              <div className="accordion-description" style={{ whiteSpace: 'pre-line', fontSize: '14px', color: '#b3b3b3' }} dangerouslySetInnerHTML={{ __html: item.description }}></div>
                            )}
                            <span className="accordion-date">{item.date}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {displayLitigation && (
          <EditDetails
            isOpen={isEditOpen}
            onClose={closeEditPopup}
            initialData={{
              id: displayLitigation.id,
              name: displayLitigation.name || "",
              description: displayLitigation.description || "",
              issued_date: displayLitigation.issued_date || "",
              resolution_date: displayLitigation.resolution_date || displayLitigation.expiry_date || "",
              category_id: (displayLitigation.category && displayLitigation.category.id) || "",
              issued_by: displayLitigation.issued_by || displayLitigation.authority || "",
              act_id: (displayLitigation.act && displayLitigation.act.id) || "",
              assigned_to: (displayLitigation.owner && displayLitigation.owner.id) || "",
              watcher_ids: (displayLitigation.watchers && displayLitigation.watchers.map((watcher: any) => watcher.id)) || [],
              court_name: displayLitigation.court_name || "",
              case_number: displayLitigation.case_number || "",
              next_hearing_date: displayLitigation.next_hearing_date || "",
              advocate_details: displayLitigation.advocate_details || "",
              case_status: displayLitigation.case_status || "",
              company_id: (displayLitigation.company && displayLitigation.company.id) || "",
              associate_company_id: (displayLitigation.associate_company && displayLitigation.associate_company.id) || "",
              location_id: (displayLitigation.location && displayLitigation.location.id) || "",
              documents: displayLitigation.documents || [],
            }}
            onSave={handleSave}
            isLoading={updating}
            onDocumentDelete={() => {
              if (refetchLitigation) refetchLitigation();
              if (refetch) refetch();
            }}
          />
        )}
      </div>
      {isLoading && <PageLoader />}
    </>
  );
};

export default LitigationDetail;